import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useSnackbar } from 'notistack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import { kycApi, userApi } from '../../services/api';

const KycDocumentDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [comment, setComment] = useState('');
  
  // Получение данных о документе
  const { data, isLoading, error } = useQuery(
    ['kycDocument', id],
    () => kycApi.getDocumentById(Number(id)),
    {
      enabled: !!id,
    }
  );
  
  // Получение данных о пользователе
  const { data: userData, isLoading: isUserLoading } = useQuery(
    ['user', data?.data.userId],
    () => userApi.getUserById(data?.data.userId as number),
    {
      enabled: !!data?.data.userId,
    }
  );
  
  // Мутация для обновления статуса документа
  const updateStatusMutation = useMutation(
    ({ status, comment }: { status: 'pending' | 'approved' | 'rejected', comment?: string }) => 
      kycApi.updateDocumentStatus(Number(id), status, comment),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kycDocument', id]);
        enqueueSnackbar('Статус документа успешно обновлен', { variant: 'success' });
        setStatusDialogOpen(false);
      },
      onError: () => {
        enqueueSnackbar('Ошибка при обновлении статуса документа', { variant: 'error' });
      },
    }
  );
  
  // Обработчик открытия диалога изменения статуса
  const handleOpenStatusDialog = () => {
    setSelectedStatus(data?.data.status || 'pending');
    setComment(data?.data.comment || '');
    setStatusDialogOpen(true);
  };
  
  // Обработчик закрытия диалога
  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
  };
  
  // Обработчик изменения выбранного статуса
  const handleStatusChange = (event: SelectChangeEvent<'pending' | 'approved' | 'rejected'>) => {
    setSelectedStatus(event.target.value as 'pending' | 'approved' | 'rejected');
  };
  
  // Обработчик изменения комментария
  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };
  
  // Обработчик сохранения статуса
  const handleSaveStatus = () => {
    updateStatusMutation.mutate({ 
      status: selectedStatus, 
      comment: comment.trim() ? comment : undefined 
    });
  };
  
  // Функция для отображения типа документа
  const getDocumentTypeText = (type: string) => {
    switch (type) {
      case 'passport':
        return 'Паспорт';
      case 'driverLicense':
        return 'Водительские права';
      case 'idCard':
        return 'Удостоверение личности';
      case 'proofOfAddress':
        return 'Подтверждение адреса';
      default:
        return type;
    }
  };
  
  if (isLoading || isUserLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !data) {
    return (
      <Alert severity="error">
        Ошибка при загрузке данных документа. Пожалуйста, попробуйте позже.
      </Alert>
    );
  }
  
  const document = data.data;
  const user = userData?.data;
  
  return (
    <Grid container spacing={3}>
      {/* Заголовок и кнопка назад */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/kyc')}
            sx={{ mr: 2 }}
          >
            Назад
          </Button>
          <Typography variant="h4">
            KYC Документ #{document.id}
          </Typography>
        </Box>
      </Grid>
      
      {/* Основная информация о документе */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Основная информация
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              ID пользователя
            </Typography>
            <Typography variant="body1">
              <Button 
                sx={{ p: 0, minWidth: 'auto' }}
                onClick={() => navigate(`/users/${document.userId}`)}
              >
                {document.userId}
              </Button>
            </Typography>
          </Box>
          
          {user && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Пользователь
              </Typography>
              <Typography variant="body1">
                {user.firstName} {user.lastName}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Тип документа
            </Typography>
            <Typography variant="body1">
              {getDocumentTypeText(document.type)}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Дата загрузки
            </Typography>
            <Typography variant="body1">
              {format(new Date(document.createdAt), 'dd.MM.yyyy HH:mm:ss', { locale: ru })}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Статус
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {document.status === 'approved' ? (
                <Chip icon={<VerifiedUserIcon />} label="Подтвержден" color="success" />
              ) : document.status === 'pending' ? (
                <Chip icon={<PendingIcon />} label="На проверке" color="warning" />
              ) : (
                <Chip icon={<CancelIcon />} label="Отклонен" color="error" />
              )}
              
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ ml: 2 }}
                onClick={handleOpenStatusDialog}
              >
                Изменить
              </Button>
            </Box>
          </Box>
          
          {document.comment && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Комментарий
              </Typography>
              <Typography variant="body1">
                {document.comment}
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>
      
      {/* Предпросмотр документа */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Документ
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button 
              variant="contained" 
              onClick={() => window.open(document.fileUrl, '_blank')}
            >
              Открыть документ в новой вкладке
            </Button>
          </Box>
          
          {/* Предпросмотр изображения, если это изображение */}
          {document.fileUrl.match(/\.(jpeg|jpg|gif|png)$/i) && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <img 
                src={document.fileUrl} 
                alt="KYC документ" 
                style={{ maxWidth: '100%', maxHeight: '400px' }} 
              />
            </Box>
          )}
          
          {/* Для PDF файлов можно использовать iframe */}
          {document.fileUrl.match(/\.(pdf)$/i) && (
            <Box sx={{ height: '500px', mt: 2 }}>
              <iframe 
                src={document.fileUrl} 
                title="KYC документ" 
                width="100%" 
                height="100%" 
                style={{ border: 'none' }}
              />
            </Box>
          )}
        </Paper>
      </Grid>
      
      {/* Диалог изменения статуса документа */}
      <Dialog open={statusDialogOpen} onClose={handleCloseStatusDialog}>
        <DialogTitle>Изменение статуса документа</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Выберите новый статус для документа #{document.id}.
          </DialogContentText>
          <FormControl fullWidth margin="dense">
            <InputLabel id="status-label">Статус</InputLabel>
            <Select
              labelId="status-label"
              value={selectedStatus}
              label="Статус"
              onChange={handleStatusChange}
            >
              <MenuItem value="pending">На проверке</MenuItem>
              <MenuItem value="approved">Подтвержден</MenuItem>
              <MenuItem value="rejected">Отклонен</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            margin="dense"
            id="comment"
            label="Комментарий"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={comment}
            onChange={handleCommentChange}
            helperText="Добавьте комментарий, особенно если отклоняете документ"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Отмена</Button>
          <Button 
            onClick={handleSaveStatus} 
            variant="contained" 
            disabled={updateStatusMutation.isLoading}
          >
            {updateStatusMutation.isLoading ? <CircularProgress size={24} /> : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default KycDocumentDetailsPage; 